import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { useIntentProductCategories } from '@/api/settings';
import {
  AddOptionRow,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxStatus,
  ComboboxValue,
  DebouncedCombobox,
  InfiniteComboboxList,
  useAddableCombobox,
  type ComboboxProps,
} from '@/components/Form/pickers';

const IntentProductCategoryMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  className,
  disabled = false,
  size = 'large',
  placeholder,
  'aria-invalid': ariaInvalid,
  ...rest
}: Omit<
  ComboboxProps<string, true>,
  'items' | 'multiple' | 'value' | 'onValueChange' | 'itemToStringValue' | 'itemToStringLabel'
> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  value?: string[];
  isLoading?: boolean;
}) => {
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const { productCategories, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useIntentProductCategories({ search });

  const items = useMemo(
    () =>
      Array.from(
        new Set(
          (productCategories ?? [])
            .map((option) => option.name ?? '')
            .filter((name) => name.length > 0),
        ),
      ),
    [productCategories],
  );

  const { isAddOption, isAddOptionSelected, itemsWithAddOption } = useAddableCombobox({
    inputSearch,
    value,
    items,
  });

  return (
    <DebouncedCombobox
      data-testid="intent-product-category-multiselect"
      multiple
      {...rest}
      filteredItems={itemsWithAddOption}
      isLoading={isLoadingProp || isLoading || isFetchingNextPage}
      disabled={disabled}
      size={size}
      value={value ?? null}
      onInputValueChange={(nextSearch) => setInputSearch(nextSearch)}
      onSearch={setSearch}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {(value ?? []).map((item) => (
            <ComboboxChip key={item}>{item}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>No product categories were found...</Trans>
        </ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
        >
          {(item: string) => (
            <ComboboxItem key={item} value={item}>
              {isAddOption(item) ? (
                <AddOptionRow label={item} isSelected={isAddOptionSelected} />
              ) : (
                item
              )}
            </ComboboxItem>
          )}
        </InfiniteComboboxList>
      </ComboboxContent>
    </DebouncedCombobox>
  );
};

export default IntentProductCategoryMultiSelect;
